import { refreshEnvironmentFiles } from "@/lib/environment-files";
import { useChatStore } from "@/stores/useChatStore";
import { useToastStore } from "@/stores/useToastStore";

export type EditorSaveOutcome = "saved" | "conflict" | "error" | "skipped";

export interface RegisteredEditor {
	/** トーストを出さずに保存し、結果を返す。 */
	saveSilently: () => Promise<EditorSaveOutcome>;
	/** 未保存の編集を破棄し、直前に読み込んだ内容へ戻す。 */
	discard: () => void;
}

const editors = new Map<string, RegisteredEditor>();

/** 指定したファイルのエディタを一括保存対象として登録する。 */
export function registerEditor(fileId: string, editor: RegisteredEditor): void {
	editors.set(fileId, editor);
}

/** 指定したファイルのエディタを一括保存対象から外す。 */
export function unregisterEditor(fileId: string): void {
	editors.delete(fileId);
}

/**
 * 未保存の全ファイルの編集を破棄する。移動先のチャットでも同じファイルを開いていると
 * エディタが作り直されず編集が残るため、破棄を選んだら移動の前に呼ぶ。
 */
export function discardAllDirtyFiles(): void {
	for (const fileId of useChatStore.getState().dirtyFileIds) editors.get(fileId)?.discard();
}

/**
 * 未保存の全ファイルを保存し、結果をサマリトースト1件で通知する。すべて保存できたかを返す。
 * dirty の解除は再描画後に store へ反映されるため、直後の dirtyFileIds ではなくこの戻り値で判定する。
 */
export async function saveAllDirtyFiles(): Promise<boolean> {
	const dirtyFileIds = useChatStore.getState().dirtyFileIds;
	const targets = dirtyFileIds
		.map((fileId) => editors.get(fileId))
		.filter((editor): editor is RegisteredEditor => editor !== undefined);
	if (targets.length === 0) return dirtyFileIds.length === 0;

	const outcomes = await Promise.all(
		targets.map((editor) => editor.saveSilently().catch(() => "error" as const)),
	);
	const saved = outcomes.filter((outcome) => outcome === "saved").length;
	const failed = outcomes.filter((outcome) => outcome === "conflict" || outcome === "error").length;
	const allSaved = targets.length === dirtyFileIds.length && saved === targets.length;
	if (saved === 0 && failed === 0) return allSaved;

	const environmentId = useChatStore.getState().environmentId;
	if (saved > 0 && environmentId) await refreshEnvironmentFiles(environmentId);

	if (failed === 0) {
		useToastStore.getState().show({
			type: "success",
			message: `${saved}件のファイルを保存しました。`,
		});
		return allSaved;
	}

	useToastStore.getState().show({
		type: "error",
		message: `${saved}件を保存しました。${failed}件は保存できませんでした。`,
	});
	return false;
}
